import { Accordion, Button, MenuTrigger, Stack, Text } from '@eleveo/components-react';
import { FilterClearIcon, ThreeDotsIcon } from '@eleveo/icons-react';
import { equals } from 'ramda';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  ACTIVE_SIDEBAR_FILTERS_COUNTER,
  APPLY_FILTER_BUTTON_TAG,
  FILTER_SIDEBAR_DROPDOWN_TOGGLE,
  RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
} from './constants';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import {
  CheckboxFilterTile,
  getCheckboxFilterTileSubtitle,
} from '../../components/LayoutNext/components/CheckboxFilterTile';
import { QueuesResult, ShiftTemplatesResult, SkillsAllResult, SupervisedTeamsResult } from '../../graphql/types';

import type { CheckboxFilterTileProps } from '../../components/LayoutNext/components/CheckboxFilterTile';

export type AgentsSidebarFilterState = {
  queues: string[];
  shiftTemplates: string[];
  skills: string[];
  teams: string[];
};

export type AgentsFilterSidebarProps = {
  handleCancel: () => void;
  preOpenedItemIds: string[];
  queuesResult: QueuesResult;
  setPreOpenedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSidebarFilter: (filter: AgentsSidebarFilterState) => void;
  shiftTemplatesResult: ShiftTemplatesResult;
  sidebarFilter: AgentsSidebarFilterState;
  skillsResult: SkillsAllResult;
  teamsResult: SupervisedTeamsResult;
};

export const AgentsFilterSidebar: React.FC<AgentsFilterSidebarProps> = ({
  handleCancel,
  preOpenedItemIds,
  queuesResult,
  setPreOpenedItemIds,
  setSidebarFilter,
  shiftTemplatesResult,
  sidebarFilter,
  skillsResult,
  teamsResult,
}) => {
  const { t } = useTranslation();

  const queues = queuesResult.data?.queues ?? [];
  const skills = skillsResult.data?.skills ?? [];
  const teams = teamsResult.data?.supervisedTeams ?? [];
  const shiftTemplates = shiftTemplatesResult.data?.shiftTemplates?.shiftTemplates ?? [];

  const defaultSSelectedQueueIds = sidebarFilter.queues;
  const defaultSelectedSkillIds = sidebarFilter.skills;
  const defaultSelectedShiftTemplateIds = sidebarFilter.shiftTemplates;
  const defaultSelectedTeamIds = sidebarFilter.teams;

  // Filters states
  const [selectedQueueIds, setSelectedQueueIds] = useState(defaultSSelectedQueueIds);
  const [selectedSkillIds, setSelectedSkillIds] = useState(defaultSelectedSkillIds);
  const [selectedTeamIds, setSelectedTeamIds] = useState(defaultSelectedTeamIds);
  const [selectedShiftTemplateIds, setSelectedShiftTemplateIds] = useState(defaultSelectedShiftTemplateIds);

  const handleApply = () => {
    setSidebarFilter({
      queues: selectedQueueIds,
      skills: selectedSkillIds,
      shiftTemplates: selectedShiftTemplateIds,
      teams: selectedTeamIds,
    });

    handleCancel();
  };

  const handleResetAllFilters = () => {
    setSelectedQueueIds([]);
    setSelectedSkillIds([]);
    setSelectedShiftTemplateIds([]);
    setSelectedTeamIds([]);

    setPreOpenedItemIds([]);
  };

  const countOfActiveFilters = [
    !!defaultSSelectedQueueIds.length,
    !!defaultSelectedSkillIds.length,
    !!defaultSelectedShiftTemplateIds.length,
    !!defaultSelectedTeamIds.length,
  ].filter((active) => active).length;

  const isFilterActive =
    selectedQueueIds.length > 0 ||
    selectedSkillIds.length > 0 ||
    selectedShiftTemplateIds.length > 0 ||
    selectedTeamIds.length > 0;

  const isApplyActive =
    equals(selectedQueueIds, defaultSSelectedQueueIds) &&
    equals(selectedSkillIds, defaultSelectedSkillIds) &&
    equals(selectedShiftTemplateIds, defaultSelectedShiftTemplateIds) &&
    equals(selectedTeamIds, defaultSelectedTeamIds);

  // Accordion filter options
  const filters: CheckboxFilterTileProps[] = [
    {
      testId: 'agents-sidebar-filter-queues',
      id: '1',
      items: queues,
      onSelectionChange: setSelectedQueueIds,
      selectedKeys: selectedQueueIds,
      subtitle: getCheckboxFilterTileSubtitle(t, queues, selectedQueueIds),
      subtitleColor: 'info',
      title: t('queue.queues'),
    },
    {
      testId: 'agents-sidebar-filter-skills',
      id: '2',
      items: skills,
      onSelectionChange: setSelectedSkillIds,
      selectedKeys: selectedSkillIds,
      subtitle: getCheckboxFilterTileSubtitle(t, skills, selectedSkillIds),
      subtitleColor: 'info',
      title: t('skill.skills'),
    },
    {
      testId: 'agents-sidebar-filter-shiftTemplates',
      id: '3',
      items: shiftTemplates,
      onSelectionChange: setSelectedShiftTemplateIds,
      selectedKeys: selectedShiftTemplateIds,
      subtitle: getCheckboxFilterTileSubtitle(t, shiftTemplates, selectedShiftTemplateIds),
      subtitleColor: 'info',
      title: t('shiftTemplate.shiftTemplate', { count: 2 }),
    },
    {
      testId: 'agents-sidebar-filter-teams',
      id: '4',
      items: teams,
      onSelectionChange: setSelectedTeamIds,
      selectedKeys: selectedTeamIds,
      subtitle: getCheckboxFilterTileSubtitle(t, teams, selectedTeamIds),
      title: t('team.team', { count: 2 }),
      subtitleColor: 'info',
    },
  ];

  return (
    <Sidebar onClose={handleCancel} title={t('filter.filters')}>
      <SidebarPanel>
        <Stack direction="horizontal" className="w-full items-center justify-between">
          <Button testId={APPLY_FILTER_BUTTON_TAG} isDisabled={isApplyActive} color="success" onClick={handleApply}>
            {t('form.apply')}
          </Button>
          <Text
            color={!countOfActiveFilters ? { light: 'grey-500', dark: 'grey-300' } : {}}
            testId={ACTIVE_SIDEBAR_FILTERS_COUNTER}
          >
            <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
              {/* space needs to be added within strong tag so the tests can run correctly */}
              {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
              <strong>{{ countOfActiveFilters }} </strong>Active filters
            </Trans>
          </Text>

          <MenuTrigger
            items={[
              {
                id: '1',
                text: t('filter.resetAllFilters'),
                onClick: handleResetAllFilters,
                icon: <FilterClearIcon />,
                isDisabled: !isFilterActive,
                testId: RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
              },
            ]}
          >
            <Button aria-label={t('filter.options')} variant="outline" testId={FILTER_SIDEBAR_DROPDOWN_TOGGLE}>
              <ThreeDotsIcon />
            </Button>
          </MenuTrigger>
        </Stack>
      </SidebarPanel>

      <SidebarContent>
        <SidebarPart>
          <Accordion
            expandedKeys={preOpenedItemIds}
            items={filters}
            expansionMode="single"
            onExpandedChange={(keys) => setPreOpenedItemIds([...keys] as string[])}
          >
            {(item) => <CheckboxFilterTile {...item} />}
          </Accordion>
        </SidebarPart>
      </SidebarContent>
    </Sidebar>
  );
};
